import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  Upload,
  X,
  Send,
  Loader2,
  FileText,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { submitCareer } from "@/lib/api";
import CornerMarks from "@/components/site/CornerMarks";

const POSITIONS = [
  { value: "solar_pv_engineer", label: "Solar PV Engineer" },
  { value: "mep_engineer", label: "MEP Engineer" },
  { value: "electrical_technician", label: "Electrical Technician" },
  { value: "project_manager", label: "Project Manager" },
  { value: "operations_maintenance", label: "Operations & Maintenance" },
  { value: "internship", label: "Internship / Attachment" },
  { value: "other", label: "Other (specify)" },
];

const MAX_MB = 5;
const ALLOWED_EXT = [".pdf", ".doc", ".docx"];
const ALLOWED_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  position: "",
  position_other: "",
  message: "",
  website: "", // honeypot
};

const Careers = () => {
  const [form, setForm] = useState(INITIAL);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validateFile = (f) => {
    if (!f) return null;
    const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error("CV must be a PDF, DOC or DOCX file.");
      return null;
    }
    if (f.type && !ALLOWED_MIME.includes(f.type) && f.type !== "") {
      // Some browsers report "" for .doc — accept ext-check as source of truth
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`CV must be under ${MAX_MB} MB.`);
      return null;
    }
    return f;
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    const valid = validateFile(f);
    if (valid) setFile(valid);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    const valid = validateFile(f);
    if (valid) setFile(valid);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your full name.");
    if (!isValidEmail(form.email)) return toast.error("Please enter a valid email.");
    if (!form.position) return toast.error("Please choose a role.");
    if (form.position === "other" && !form.position_other.trim())
      return toast.error("Please specify the role you're applying for.");
    if (!form.message.trim() || form.message.trim().length < 20)
      return toast.error("Tell us a little about yourself (at least 20 characters).");

    try {
      setLoading(true);
      await submitCareer(
        {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim() || undefined,
          position: form.position,
          position_other: form.position_other.trim() || undefined,
          message: form.message.trim(),
          website: form.website,
        },
        file || undefined
      );
      toast.success(
        "Application received. Our team will review and reach out shortly."
      );
      setForm(INITIAL);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    color: "var(--text)",
  };

  return (
    <section
      id="careers"
      aria-label="Careers at TAFRA Energy"
      data-testid="careers-section"
      className="relative py-20 sm:py-24 lg:py-28 overflow-hidden"
      style={{ background: "var(--bg-soft)" }}
    >
      <div className="absolute inset-0 energy-grid-bg opacity-[0.18] pointer-events-none" />
      <span
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, var(--accent-crimson-hover), transparent)",
          opacity: 0.4,
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left — pitch */}
          <motion.div
            initial={{ y: 18, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <span className="eyebrow inline-flex items-center gap-2">
              <Briefcase className="w-3.5 h-3.5" strokeWidth={1.8} />
              Careers · we&apos;re hiring
            </span>
            <h2
              className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-[1.05]"
              style={{ color: "var(--text)" }}
            >
              Build with us.{" "}
              <span className="font-medium">Power what&apos;s next.</span>
            </h2>
            <p
              className="mt-6 text-base leading-relaxed"
              style={{ color: "var(--text-muted)" }}
            >
              TAFRA Energy is scaling its engineering bench across solar PV, MEP
              and O&M. If you&apos;re meticulous and want to build things that
              last, send us your CV and a short note about the work
              you&apos;d like to do next.
            </p>

            <ul
              className="mt-8 space-y-3 text-sm"
              style={{ color: "var(--text)" }}
            >
              {[
                "Real projects from day one — end-to-end delivery",
                "Design → Build → Commission → Maintain scope",
                "Nairobi HQ · nationwide field deployments",
              ].map((li) => (
                <li key={li} className="flex items-start gap-3">
                  <span
                    className="mt-1.5 w-1.5 h-1.5"
                    style={{
                      background: "var(--accent-crimson-hover)",
                      transform: "rotate(45deg)",
                    }}
                    aria-hidden
                  />
                  <span>{li}</span>
                </li>
              ))}
            </ul>

            <div
              className="mt-8 flex items-center gap-2 pt-6"
              style={{ borderTop: "1px dashed var(--border)" }}
            >
              <ShieldCheck
                className="w-3.5 h-3.5"
                strokeWidth={1.8}
                style={{ color: "var(--accent-blue)" }}
              />
              <span
                className="font-mono-spec text-[10px] tracking-[0.22em] uppercase"
                style={{ color: "var(--text-muted)" }}
              >
                Your CV is stored securely · never shared
              </span>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div
              className="relative p-6 sm:p-8 btn-sharp backdrop-blur-xl"
              style={{
                background: "var(--surface-elev)",
                border: "1px solid var(--border)",
              }}
            >
              <span
                aria-hidden
                className="absolute -top-px left-10 right-10 h-px"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, var(--accent-crimson-hover), transparent)",
                }}
              />

              <form
                onSubmit={onSubmit}
                data-testid="careers-form"
                className="space-y-4"
              >
                {/* Honeypot */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={onChange("website")}
                  data-testid="careers-honeypot"
                  aria-hidden="true"
                  className="absolute opacity-0 -z-10 pointer-events-none w-0 h-0"
                  name="website"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field
                    label="Full name"
                    testid="careers-name-input"
                    value={form.name}
                    onChange={onChange("name")}
                    required
                    placeholder="Your name"
                    inputStyle={inputStyle}
                  />
                  <Field
                    label="Email"
                    type="email"
                    testid="careers-email-input"
                    value={form.email}
                    onChange={onChange("email")}
                    required
                    placeholder="you@company.com"
                    inputStyle={inputStyle}
                  />
                </div>

                <Field
                  label="Phone (optional)"
                  type="tel"
                  testid="careers-phone-input"
                  value={form.phone}
                  onChange={onChange("phone")}
                  placeholder="+254 ..."
                  inputStyle={inputStyle}
                />

                <div>
                  <label
                    className="block text-[11px] tracking-[0.2em] uppercase mb-2"
                    style={{ color: "var(--text-faint)" }}
                  >
                    Role you&apos;re applying for
                  </label>
                  <select
                    value={form.position}
                    onChange={onChange("position")}
                    required
                    data-testid="careers-position-select"
                    className="w-full btn-sharp px-4 py-3 focus:outline-none"
                    style={inputStyle}
                  >
                    <option value="" disabled>
                      Choose a role…
                    </option>
                    {POSITIONS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                {form.position === "other" && (
                  <Field
                    label="Specify role"
                    testid="careers-position-other-input"
                    value={form.position_other}
                    onChange={onChange("position_other")}
                    required
                    placeholder="e.g. HSE Officer, Structural Design Engineer…"
                    inputStyle={inputStyle}
                  />
                )}

                <div>
                  <label
                    className="block text-[11px] tracking-[0.2em] uppercase mb-2"
                    style={{ color: "var(--text-faint)" }}
                  >
                    Message
                  </label>
                  <textarea
                    value={form.message}
                    onChange={onChange("message")}
                    required
                    rows={5}
                    data-testid="careers-message-input"
                    className="w-full btn-sharp px-4 py-3 resize-none focus:outline-none"
                    style={inputStyle}
                    placeholder="Tell us about your experience, projects you've delivered and what you'd like to work on next…"
                  />
                </div>

                {/* CV Upload */}
                <div>
                  <label
                    className="block text-[11px] tracking-[0.2em] uppercase mb-2"
                    style={{ color: "var(--text-faint)" }}
                  >
                    CV / Resume · PDF, DOC, DOCX · max 5 MB
                  </label>

                  {!file ? (
                    <label
                      htmlFor="careers-cv-input"
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={onDrop}
                      className="flex flex-col items-center justify-center gap-2 px-6 py-8 cursor-pointer btn-sharp transition-colors"
                      style={{
                        background: dragOver
                          ? "var(--accent-crimson-soft)"
                          : "var(--surface)",
                        border: `1px dashed ${
                          dragOver
                            ? "var(--accent-crimson-hover)"
                            : "var(--border-strong)"
                        }`,
                      }}
                      data-testid="careers-cv-dropzone"
                    >
                      <Upload
                        className="w-5 h-5"
                        strokeWidth={1.6}
                        style={{ color: "var(--accent-crimson-hover)" }}
                      />
                      <span
                        className="text-sm text-center"
                        style={{ color: "var(--text-muted)" }}
                      >
                        <span
                          className="font-medium"
                          style={{ color: "var(--text)" }}
                        >
                          Click to upload
                        </span>{" "}
                        or drag & drop
                      </span>
                      <span
                        className="font-mono-spec text-[10px] tracking-[0.22em] uppercase"
                        style={{ color: "var(--text-faint)" }}
                      >
                        PDF · DOC · DOCX
                      </span>
                    </label>
                  ) : (
                    <div
                      className="flex items-center justify-between gap-3 px-4 py-3 btn-sharp"
                      style={{
                        background: "var(--surface)",
                        border: "1px solid var(--accent-blue)",
                      }}
                      data-testid="careers-cv-preview"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText
                          className="w-4 h-4 shrink-0"
                          strokeWidth={1.8}
                          style={{ color: "var(--accent-blue)" }}
                        />
                        <div className="min-w-0">
                          <p
                            className="text-sm truncate"
                            style={{ color: "var(--text)" }}
                          >
                            {file.name}
                          </p>
                          <p
                            className="font-mono-spec text-[10px] tracking-[0.18em] uppercase"
                            style={{ color: "var(--text-faint)" }}
                          >
                            {(file.size / 1024).toFixed(0)} KB · attached
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFile(null);
                          if (fileInputRef.current)
                            fileInputRef.current.value = "";
                        }}
                        data-testid="careers-cv-remove"
                        aria-label="Remove CV"
                        className="w-8 h-8 flex items-center justify-center btn-sharp shrink-0"
                        style={{
                          border: "1px solid var(--border)",
                          color: "var(--text-muted)",
                        }}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    id="careers-cv-input"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={onPickFile}
                    data-testid="careers-cv-input"
                    className="hidden"
                  />
                </div>

                <div className="flex items-center justify-between gap-3 pt-2">
                  <p
                    className="text-[11px]"
                    style={{ color: "var(--text-faint)" }}
                  >
                    Spam-protected. We reply within one business week.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="careers-submit-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 font-semibold btn-sharp transition-all disabled:opacity-60"
                    style={{
                      background: "var(--accent-crimson)",
                      color: "#FFFFFF",
                      boxShadow: "0 0 18px var(--accent-crimson-soft)",
                    }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit application
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      <CornerMarks code="SEC—05.CAREERS" />
    </section>
  );
};

const Field = ({ label, type = "text", testid, value, onChange, required, placeholder, inputStyle }) => (
  <div>
    <label
      className="block text-[11px] tracking-[0.2em] uppercase mb-2"
      style={{ color: "var(--text-faint)" }}
    >
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full btn-sharp px-4 py-3 focus:outline-none"
      style={inputStyle}
      placeholder={placeholder}
    />
  </div>
);

export default Careers;
